// Dienstleister-Vorschlag für Tickets (Handoff Dienstleister §7): Regel → Gewerk → bevorzugt, dann Bewertung.
// Inaktive werden nie vorgeschlagen; bei Notfall-Regeln nur Notfallkontakte.

export interface VorschlagKandidat {
  id: string;
  name: string;
  aktiv: boolean;
  notfall: boolean;
  bevorzugt: boolean;
  /** bevorzugt, aber nur als Vertretung */
  vertretung?: boolean;
  bewertung: number | null;
  kategorieIds: readonly string[];
}

export interface RegelKurz { gewerk_id: string | null; ist_notfall: boolean; aktiv?: boolean }

/** Rang: bevorzugt (erste Wahl) → bevorzugt (Vertretung) → übrige. */
const rang = (k: VorschlagKandidat) => (k.bevorzugt ? (k.vertretung ? 1 : 0) : 2);

export function vorschlaege<K extends VorschlagKandidat>(regel: RegelKurz, kandidaten: readonly K[]): K[] {
  if (!regel.gewerk_id) return [];
  return kandidaten
    .filter(k => k.aktiv && k.kategorieIds.includes(regel.gewerk_id as string) && (!regel.ist_notfall || k.notfall))
    .sort((a, b) => rang(a) - rang(b) || (b.bewertung ?? 0) - (a.bewertung ?? 0) || a.name.localeCompare(b.name, 'de'));
}

export function besterVorschlag<K extends VorschlagKandidat>(regel: RegelKurz, kandidaten: readonly K[]): K | null {
  return vorschlaege(regel, kandidaten)[0] ?? null;
}

// ── Regeln ─────────────────────────────────────────────────────────────────────

export type Prioritaet = 'kritisch' | 'hoch' | 'mittel' | 'niedrig';
export const PRIORITAETEN: readonly { key: Prioritaet; label: string; ton: 'bad' | 'warn' | 'info' | 'neutral' }[] = [
  { key: 'kritisch', label: 'kritisch', ton: 'bad' }, { key: 'hoch', label: 'hoch', ton: 'warn' },
  { key: 'mittel', label: 'mittel', ton: 'info' }, { key: 'niedrig', label: 'niedrig', ton: 'neutral' },
];
export const prioTon = (p: string) => PRIORITAETEN.find(x => x.key === p)?.ton ?? 'neutral';

/** „wasser, Leck ,rohr“ → ['wasser','leck','rohr'] (klein, ohne Doppelte). */
export function stichwoerter(roh: string): string[] {
  return [...new Set(roh.split(',').map(s => s.trim().toLowerCase()).filter(Boolean))];
}

export interface RegelEntwurf {
  name: string; stichwoerter: string; prioritaet: Prioritaet; ist_notfall: boolean; nur_mit_anhang: boolean; ist_rechtlich_sensibel: boolean;
  kategorie_id: string; gewerk_id: string; bearbeiter_team_id: string; aktiv: boolean;
}

export function regelFehler(e: RegelEntwurf): string | null {
  if (!e.name.trim()) return 'Bezeichnung fehlt';
  if (stichwoerter(e.stichwoerter).length === 0) return 'Mindestens ein Stichwort';
  if (!e.kategorie_id && !e.gewerk_id) return 'Ticket-Kategorie oder Gewerk wählen';
  return null;
}

export function regelPayload(e: RegelEntwurf): Record<string, string | boolean | string[] | null> {
  return {
    name: e.name.trim(), stichwoerter: stichwoerter(e.stichwoerter), prioritaet: e.prioritaet, ist_notfall: e.ist_notfall,
    nur_mit_anhang: e.nur_mit_anhang, ist_rechtlich_sensibel: e.ist_rechtlich_sensibel, aktiv: e.aktiv,
    kategorie_id: e.kategorie_id || null, gewerk_id: e.gewerk_id || null, bearbeiter_team_id: e.bearbeiter_team_id || null,
  };
}

/** Kategorie endgültig löschen nur ohne Dienstleister und ohne Regel (Entscheidung 14.10.2026), sonst deaktivieren. */
export function kategorieLoeschbar(anzahlDienstleister: number, anzahlRegeln: number): boolean {
  return anzahlDienstleister === 0 && anzahlRegeln === 0;
}
